import { useEffect, useState } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { usePeerReviewDraft } from '@/components/reviews/usePeerReviewDraft'
import { ReviewDecision } from '@/components/reviews/ReviewDecision'
import { ReviewResizeHandle } from '@/components/reviews/ReviewResizeHandle'
import type { TemplateDetail } from '@/lib/scientific'

export function CollectionQuality({ data, selected, developer, busy, action, onDirty }: {
  data: TemplateDetail; selected: string; developer: boolean; busy: boolean
  action: (path: string, body: unknown) => Promise<boolean>; onDirty: (dirty: boolean) => void
}) {
  const { user } = useAuth()
  const { draft, update, clear } = usePeerReviewDraft('', `science-qc:${user?.id}:${data.template.collection.revision_id}:${data.round.round_id}:${developer ? 'collection' : selected}:${developer}`)
  const [affected, setAffected] = useState(data.tasks.map(t => t.task_id))
  const [commentSaved, setCommentSaved] = useState(false)
  useEffect(() => { onDirty(!!draft.feedback || !!draft.verdict); return () => onDirty(false) }, [draft.feedback, draft.verdict, onDirty])
  const open = data.round.kind === 'quality' && data.round.status === 'open'
  if (!open) return <aside className="peer-decision"><h3>Quality check</h3><p>{data.round.kind === 'quality' ? 'This quality check is closed. Its evidence remains available.' : 'The quality check opens on its own when the builder has graded its tasks.'}</p></aside>
  if (!developer && !data.can_review) return <aside className="peer-decision"><h3>Scientific feedback</h3><p>Claim the quality check to write feedback. All evidence is available to read.</p>
    {data.can_claim && <button disabled={busy} onClick={() => void action('claim', { version: data.round.version })}>Claim quality check</button>}</aside>
  // The quality check decides on the whole workflow; a single task only gets a comment for the builder.
  if (!developer && selected) return <aside className="peer-decision" aria-label="Task comment">
    <ReviewResizeHandle />
    <header className="peer-decision-heading"><h3>Comment on this task</h3></header>
    <form onSubmit={async event => {
      event.preventDefault()
      if (busy || !draft.feedback.trim()) return
      if (await action('feedback', { version: data.round.version, task_id: selected, feedback: draft.feedback.trim() })) {
        clear(); setCommentSaved(true)
      }
    }}>
      <label htmlFor="task-comment">Comment</label>
      <textarea id="task-comment" rows={10} maxLength={8000} value={draft.feedback} disabled={busy}
        placeholder="What is wrong or worth keeping in this task…" onChange={event => { update('feedback', event.target.value); setCommentSaved(false) }} />
      <div className="peer-decision-actions"><button className="primary" disabled={busy || !draft.feedback.trim()}>
        {busy ? 'Saving…' : 'Save task comment'}
      </button></div>
      {commentSaved && <p className="small" role="status">Comment saved.</p>}
    </form>
  </aside>
  return <ReviewDecision quality busy={busy} submitted={false} draft={draft} update={update}
    title={developer ? 'Decision for the whole workflow' : 'Your recommendation for the whole workflow'}
    submitLabel={developer ? 'Save decision' : 'Save recommendation'}
    submitDisabled={developer && draft.verdict === 'ready' && !!data.publish_blockers?.length}
    submit={async () => {
      const ok = await action(developer ? 'quality-decision' : 'feedback', developer
        ? { verdict: draft.verdict, feedback: draft.feedback, revision: data.template.revision, version: data.round.version, task_ids: affected }
        : { verdict: draft.verdict, feedback: draft.feedback, scope: 'workflow', version: data.round.version })
      if (ok) clear()
    }}>
    {developer && <p className="small">{draft.verdict === 'needs_changes'
      ? 'Your feedback goes to the builder, which restarts on its own and opens a new quality check when it is done.'
      : 'Ready approves every included task and queues publication immediately. Scientist and agent recommendations are advisory.'}</p>}
    {developer && draft.verdict === 'needs_changes' && <fieldset><legend>Tasks that need correction</legend>{data.tasks.map(t => <label key={t.task_id} className="row"><input type="checkbox" checked={affected.includes(t.task_id)}
      onChange={e => setAffected(ids => e.target.checked ? [...ids, t.task_id] : ids.filter(id => id !== t.task_id))} />{t.title}</label>)}</fieldset>}
    {!!data.removed_tasks?.length && <section className="note"><strong>Publication will delete these tasks from HF:</strong><ul>{data.removed_tasks.map(tid => <li key={tid}>{tid}</li>)}</ul></section>}
    {developer && !!data.publish_blockers?.length && <ul className="science-blocker-list">{data.publish_blockers.map(b => <li key={b}>{b}</li>)}</ul>}
  </ReviewDecision>
}
